import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { LOGIN_ROUTE } from '../lib/constants/authRoutes';
import { discardPrintTemplateLocalDraftsOnLogout } from '../lib/print/printTemplateDraftStorage';

/**
 * Shared logout flow — server revoke, clear the local session, navigate to login. Extracted
 * (2026-09-11 thermos fix) from three near-identical copies (`AdminLayout`, `TenantLayout`,
 * `BlockedAccessPage`) that had drifted slightly (only two of the three reset `loggingOut` in a
 * `finally`), which risked the drift widening every time one copy got a fix the other two missed.
 *
 * `navigate` is a caller-supplied function rather than a hook call inside here because the three
 * call sites intentionally use different navigation: `AdminLayout` uses the view-transition
 * navigate, `TenantLayout` deliberately uses the raw router navigate for this one call (logout
 * clears auth state mid-transition), and `BlockedAccessPage` uses plain `useNavigate()`. Behaviour
 * is preserved exactly — only the duplicated state/try/catch/finally is centralised.
 */
export function useLogout(navigate: (path: string) => void) {
  const { t } = useTranslation();
  const logout = useAuthStore((s) => s.logout);
  const userEmail = useAuthStore((s) => s.user?.email);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  const handleLogout = useCallback(async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setLogoutError(null);
    try {
      await api.post('/api/v1/auth/logout', {});
      logout();
      // Explicit sign-out only: a forced session expiry (lib/api.ts redirectToLogin) keeps the
      // user's own drafts so they can recover after signing back in.
      discardPrintTemplateLocalDraftsOnLogout(userEmail);
      navigate(LOGIN_ROUTE);
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number } };
      const status = ax.response?.status;
      setLogoutError(
        status ? t('auth.logout.errorStatus', { status }) : t('auth.logout.errorNetwork'),
      );
    } finally {
      setLoggingOut(false);
    }
  }, [loggingOut, logout, navigate, t, userEmail]);

  return { handleLogout, loggingOut, logoutError };
}
